import { Controller } from '@hotwired/stimulus'
import { showElement, hideElement } from 'utils/toggle_element_visibility'
import Timer from 'utils/timer'

export default class extends Controller {
  static values = {
    startDate: String,
    endDate: String
  }

  initialize() {
    this.startDate = Date.parse(this.startDateValue)
    this.endDate = Date.parse(this.endDateValue)
    this.messageContainer = this.element.querySelector("[data-content-container]")

    if (Date.now() < this.startDate) {
      this._countdownToLive()
    } else if (Date.now() > this.startDate && Date.now() < this.endDate) {
      this._setStateCallbacks()
      this._countdownToEnd()
    } else {
      this._initEnd()
    }
  }

  _countdownToLive() {
    showElement(this.element)

    // Format date to match Figma more accurately than using local_time helper
    this.messageContainer.innerHTML = `Live webcast begins on ${this._formatDate(this.startDate)}`

    const secondsToLive = (this.startDate - Date.now()) / 1000
    const timer = new Timer(this.element, null, secondsToLive)
    timer.init()

    this.element.addEventListener("timer.finish", () => {
      this.element.removeEventListener("timer.finish", {})
      this.messageContainer.innerHTML = `Webcast is live. <a href='${window.location.href}'>Refresh page</a> to view the webcast.`
      this._countdownToEnd()
    })
  }

  _countdownToEnd() {
    const secondsToEnd = (this.endDate - Date.now()) / 1000
    const timer = new Timer(this.element, null, secondsToEnd)
    timer.init()

    this.element.addEventListener("timer.finish", () => {
      this.element.removeEventListener("timer.finish", {})
      this.messageContainer.innerHTML = `Live webcast ended on ${this._formatDate(this.endDate)}`
      showElement(this.element)
    })
  }

  _initEnd() {
    showElement(this.element)
    const date = Math.min(this.endDate, Date.now()) // If the webcast ends prematurely we use the current time
    this.messageContainer.innerHTML = `Live webcast ended on ${this._formatDate(date)}`
  }

  _setStateCallbacks() {
    document.addEventListener("webcast.started", () => {
      this.messageContainer.innerHTML = "Live webcast will begin momentarily"
      showElement(this.element)
    })

    document.addEventListener("webcast.not_started", () => {
      this.messageContainer.innerHTML = "Live webcast will begin momentarily"
      showElement(this.element)
    })

    document.addEventListener("webcast.ended", () => {
      this._initEnd()
    })

    document.addEventListener("webcast.screen_sharing.started", () => {
      this._refreshPageOnParam()

      if (navigator.userAgent.search("Safari") >= 0 && navigator.userAgent.search("Chrome") < 0) {
        this.messageContainer.innerHTML = `Live webcasts are not fully supported in Safari. If you experience any issues while watching the course please <a href="${window.location.href}">refresh the page</a>.`
      } else {
        hideElement(this.element)
      }
    })

    document.addEventListener("webcast.screen_sharing.stopped", () => {
      this.messageContainer.innerHTML = "Live webcast will begin momentarily"
      showElement(this.element)
    })
  }

  // Hack/workaround to fix the zoom player height
  // More details here: https://github.com/Quimbee/Quimbee/pull/15881
  _refreshPageOnParam() {
    const url = new URL(window.location)

    if (url.searchParams.get("refresh") !== "false") {
      url.searchParams.set("refresh", "false")
      window.location = url.href
    }
  }

  _formatDate(date) {
    const options = {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
      hour12: true
    }

    const formatter = new Intl.DateTimeFormat("en-US", options)

    return formatter.format(date)
  }
}
